// frontend/admin-portal/src/pages/CustomerDetail.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Badge,
  Alert,
  Spinner,
  Input,
  formatMoney,
} from "@fiberhome/shared-ui";
import { customerService } from "../services/customerService";
import { invoiceService } from "../services/invoiceService";
import { paymentService } from "../services/paymentService";
import { useSettings } from "../contexts/SettingsContext";
import CustomerModal from "../components/orders/CustomerModal";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get } = useSettings();
  const currency = get("finance.baseCurrency", "USD");

  const [customer, setCustomer] = useState(null);
  const [aging, setAging] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Load customer + aging in one request
      const { customer: c, aging: a } = await customerService.get(id);
      setCustomer(c);
      setAging(a);

      // Load invoices + payments in parallel
      const [invRes, payRes] = await Promise.all([
        invoiceService.list({ customerId: id, limit: 50, sortDir: "desc" }),
        paymentService.list({ customerId: id, limit: 20 }),
      ]);
      setInvoices(invRes.items || []);
      setPayments(payRes.items || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <>
        <PageHeader title="Customer" breadcrumbs={["Home", "Customers", "…"]} />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !customer) {
    return (
      <>
        <PageHeader
          title="Customer not found"
          breadcrumbs={["Home", "Customers"]}
        />
        <Alert variant="danger">{error || "Customer not found"}</Alert>
        <Button
          className="mt-3"
          variant="outline"
          onClick={() => navigate("/customers")}
        >
          ← Back to Customers
        </Button>
      </>
    );
  }

  const cur = customer.currency || currency;
  const daysAgo30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);

  const totalBilled = invoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + Number(i.paidAmount || 0), 0);

  return (
    <>
      <PageHeader
        title={customer.name}
        subtitle={[customer.email, customer.phone].filter(Boolean).join(" · ")}
        breadcrumbs={["Home", "Customers", customer.name]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(
                  `/customers/${customer.id}/statement?from=${daysAgo30}&to=${today}`,
                )
              }
            >
              Statement
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/customers")}
            >
              Back
            </Button>
          </div>
        }
      />

      {/* ── Top: info + aging ──────────────────────────────── */}
      <div className="row g-3 mb-3">
        <div className="col-lg-5">
          <Card title="Customer Info">
            <div className="fh-product-meta">
              <div className="fh-product-meta__row">
                <span className="text-muted">Name</span>
                <span style={{ fontWeight: 600 }}>{customer.name}</span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Email</span>
                <span>
                  {customer.email || <span className="text-muted">—</span>}
                </span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Phone</span>
                <span>
                  {customer.phone || <span className="text-muted">—</span>}
                </span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Address</span>
                <span style={{ textAlign: "right", fontSize: 12.5 }}>
                  {[customer.address, customer.city, customer.country]
                    .filter(Boolean)
                    .join(", ") || <span className="text-muted">—</span>}
                </span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Tax ID</span>
                <span>
                  {customer.taxId || <span className="text-muted">—</span>}
                </span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Status</span>
                <Badge variant={customer.isActive ? "success" : "default"}>
                  {customer.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
            {customer.notes && (
              <div
                style={{
                  marginTop: "0.75rem",
                  paddingTop: "0.75rem",
                  borderTop: "1px solid var(--fh-border, #eef0f3)",
                  fontSize: 12.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                <div
                  className="text-muted"
                  style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}
                >
                  NOTES
                </div>
                {customer.notes}
              </div>
            )}
          </Card>
        </div>

        <div className="col-lg-7">
          <Card title="Outstanding & Aging">
            {!aging || aging.total === 0 ? (
              <div className="text-muted" style={{ fontSize: 13 }}>
                No outstanding balance. ✅
              </div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-3">
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      Current (0–30d)
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>
                      {formatMoney(aging.current, { symbol: cur })}
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      31–60 days
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>
                      {formatMoney(aging.d30, { symbol: cur })}
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      61–90 days
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>
                      {formatMoney(aging.d60, { symbol: cur })}
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      Over 90 days
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: aging.d90plus > 0 ? "#dc3545" : undefined,
                      }}
                    >
                      {formatMoney(aging.d90plus, { symbol: cur })}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    paddingTop: "0.75rem",
                    borderTop: "1px solid var(--fh-border, #eef0f3)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      Total outstanding
                      {aging.oldestDays > 0 && (
                        <>
                          {" "}
                          · oldest {aging.oldestDays} day
                          {aging.oldestDays === 1 ? "" : "s"}
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>
                      {formatMoney(aging.total, { symbol: cur })}
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() =>
                      navigate(
                        `/customers/${customer.id}/statement?from=${daysAgo30}&to=${today}`,
                      )
                    }
                  >
                    View Statement →
                  </Button>
                </div>

                {aging.invoices?.length > 0 && (
                  <div
                    style={{
                      marginTop: "0.75rem",
                      paddingTop: "0.75rem",
                      borderTop: "1px solid var(--fh-border, #eef0f3)",
                    }}
                  >
                    <div
                      className="text-muted"
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        marginBottom: 6,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Unpaid invoices
                    </div>
                    {aging.invoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="d-flex justify-content-between align-items-center"
                        style={{
                          padding: "0.35rem 0",
                          borderBottom: "1px dashed var(--fh-border, #eef0f3)",
                          fontSize: 12.5,
                          cursor: "pointer",
                        }}
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                      >
                        <div>
                          <span
                            style={{ fontFamily: "monospace", fontWeight: 600 }}
                          >
                            {inv.code}
                          </span>
                          <span
                            className="text-muted"
                            style={{ marginLeft: 8, fontSize: 11 }}
                          >
                            {new Date(inv.issueDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          <Badge
                            variant={
                              inv.daysOverdue > 90
                                ? "danger"
                                : inv.daysOverdue > 60
                                  ? "warning"
                                  : inv.daysOverdue > 30
                                    ? "warning"
                                    : "primary"
                            }
                          >
                            {inv.daysOverdue}d overdue
                          </Badge>
                          <span style={{ fontWeight: 600 }}>
                            {formatMoney(inv.balance, { symbol: inv.currency })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>

      {/* ── Middle: summary stats ───────────────────────────── */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Card>
            <div className="text-muted" style={{ fontSize: 11 }}>
              Total invoiced
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {formatMoney(totalBilled, { symbol: cur })}
            </div>
            <div className="text-muted" style={{ fontSize: 11.5 }}>
              {invoices.length} invoice{invoices.length === 1 ? "" : "s"}
            </div>
          </Card>
        </div>
        <div className="col-md-4">
          <Card>
            <div className="text-muted" style={{ fontSize: 11 }}>
              Total paid
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#198754" }}>
              {formatMoney(totalPaid, { symbol: cur })}
            </div>
            <div className="text-muted" style={{ fontSize: 11.5 }}>
              {payments.length} payment{payments.length === 1 ? "" : "s"}
            </div>
          </Card>
        </div>
        <div className="col-md-4">
          <Card>
            <div className="text-muted" style={{ fontSize: 11 }}>
              Balance
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color:
                  Number(customer.creditBalance) > 0 ? "#dc3545" : "#198754",
              }}
            >
              {formatMoney(customer.creditBalance || 0, { symbol: cur })}
            </div>
            <div className="text-muted" style={{ fontSize: 11.5 }}>
              {Number(customer.creditBalance) > 0 ? "customer owes" : "settled"}
            </div>
          </Card>
        </div>
      </div>

      {/* ── Bottom: invoices and payments ──────────────────── */}
      <div className="row g-3">
        <div className="col-lg-7">
          <Card title={`Invoices (${invoices.length})`}>
            {invoices.length === 0 ? (
              <div className="text-muted" style={{ fontSize: 13 }}>
                No invoices yet.
              </div>
            ) : (
              <div>
                {invoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="d-flex justify-content-between align-items-center"
                    style={{
                      padding: "0.5rem 0",
                      borderBottom: "1px solid var(--fh-border, #eef0f3)",
                      fontSize: 12.5,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontFamily: "monospace" }}>
                        {inv.code}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {new Date(inv.issueDate).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="d-flex gap-3 align-items-center">
                      <Badge
                        variant={
                          inv.status === "paid"
                            ? "success"
                            : inv.status === "partial"
                              ? "warning"
                              : inv.status === "overdue"
                                ? "danger"
                                : inv.status === "void"
                                  ? "default"
                                  : "primary"
                        }
                      >
                        {inv.status}
                      </Badge>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 600 }}>
                          {formatMoney(inv.total, { symbol: inv.currency })}
                        </div>
                        {Number(inv.paidAmount) > 0 &&
                          Number(inv.paidAmount) < Number(inv.total) && (
                            <div
                              className="text-muted"
                              style={{ fontSize: 10.5 }}
                            >
                              paid{" "}
                              {formatMoney(inv.paidAmount, {
                                symbol: inv.currency,
                              })}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="col-lg-5">
          <Card title={`Recent Payments (${payments.length})`}>
            {payments.length === 0 ? (
              <div className="text-muted" style={{ fontSize: 13 }}>
                No payments yet.
              </div>
            ) : (
              <div>
                {payments.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      padding: "0.5rem 0",
                      borderBottom: "1px solid var(--fh-border, #eef0f3)",
                      fontSize: 12.5,
                    }}
                  >
                    <div className="d-flex justify-content-between">
                      <div>
                        <div
                          style={{ fontWeight: 600, fontFamily: "monospace" }}
                        >
                          {p.code}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {new Date(p.paidAt).toLocaleDateString()} · {p.method}
                          {p.invoice?.code && ` · ${p.invoice.code}`}
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, color: "#198754" }}>
                        {formatMoney(p.amount, { symbol: p.currency })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Edit modal */}
      {editOpen && (
        <CustomerModal
          open={editOpen}
          customer={customer}
          onClose={() => setEditOpen(false)}
          onSaved={async (updated) => {
            setEditOpen(false);
            await load();
          }}
        />
      )}
    </>
  );
}
